import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Button, Modal } from '@/components/ui'

/**
 * Page-level actions the global shortcut handler can invoke.
 *
 * A page registers whichever it supports; unregistered keys fall through to the
 * browser. Keeping this behind a context is what lets the shell own the key
 * listener without importing any page.
 */
export interface ShortcutHandlers {
  /** `/` — move focus to the page's search field. */
  focusSearch?: () => void
  /** `g` — generate alt text for the current page of images. */
  generatePage?: () => void
  /** `a` — approve the current selection. */
  approveSelected?: () => void
  /** `⌘/Ctrl+S` — open the save-to-WordPress flow. */
  save?: () => void
}

export interface ShortcutContextValue {
  /**
   * Registers the active page's handlers and returns a disposer. Pass a getter
   * when the handlers change every render — prefer `useRegisterShortcuts`.
   */
  register: (handlers: ShortcutHandlers | (() => ShortcutHandlers)) => () => void
  openShortcutSheet: () => void
}

const noop = (): void => {}

export const ShortcutContext = createContext<ShortcutContextValue>({
  register: () => noop,
  openShortcutSheet: noop
})

export function useShortcuts(): ShortcutContextValue {
  return useContext(ShortcutContext)
}

/** Registers handlers for as long as the calling component is mounted. */
export function useRegisterShortcuts(handlers: ShortcutHandlers): void {
  const { register } = useShortcuts()
  const latest = useRef(handlers)

  useEffect(() => {
    latest.current = handlers
  })

  useEffect(() => register(() => latest.current), [register])
}

/** A keystroke inside a text field belongs to the field, never to the app. */
function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

interface KeyboardShortcutsProps {
  children?: ReactNode
}

export default function KeyboardShortcuts({
  children
}: KeyboardShortcutsProps): React.ReactElement {
  const [sheetOpen, setSheetOpen] = useState(false)
  const getHandlers = useRef<(() => ShortcutHandlers) | null>(null)

  const register = useCallback((handlers: ShortcutHandlers | (() => ShortcutHandlers)) => {
    const getter = typeof handlers === 'function' ? handlers : (): ShortcutHandlers => handlers
    getHandlers.current = getter
    return () => {
      if (getHandlers.current === getter) getHandlers.current = null
    }
  }, [])

  const openShortcutSheet = useCallback(() => setSheetOpen(true), [])

  const value = useMemo<ShortcutContextValue>(
    () => ({ register, openShortcutSheet }),
    [register, openShortcutSheet]
  )

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const handlers = getHandlers.current?.() ?? {}

      // ⌘/Ctrl+S is a deliberate chord, so it works from inside an editor too —
      // but only claim it when a page actually handles saving.
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 's') {
        if (!handlers.save) return
        event.preventDefault()
        handlers.save()
        return
      }

      if (event.metaKey || event.ctrlKey || event.altKey) return
      if (isTypingTarget(event.target)) return

      if (event.key === '?') {
        event.preventDefault()
        setSheetOpen(true)
        return
      }

      if (event.shiftKey) return

      switch (event.key) {
        case '/': {
          const focus =
            handlers.focusSearch ??
            (() => document.querySelector<HTMLElement>('[data-shortcut-search]')?.focus())
          event.preventDefault()
          focus()
          break
        }
        case 'g':
          if (!handlers.generatePage) return
          event.preventDefault()
          handlers.generatePage()
          break
        case 'a':
          if (!handlers.approveSelected) return
          event.preventDefault()
          handlers.approveSelected()
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const isApple = /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent)
  const mod = isApple ? '⌘' : 'Ctrl'

  const rows: Array<{ keys: string[]; label: string }> = [
    { keys: ['/'], label: 'Focus search' },
    { keys: ['g'], label: 'Generate alt text for this page' },
    { keys: ['a'], label: 'Approve the selected images' },
    { keys: [mod, 'S'], label: 'Save approved alt text to WordPress' },
    { keys: ['?'], label: 'Show this sheet' },
    { keys: ['Esc'], label: 'Close a dialog, menu, or drawer' }
  ]

  return (
    <ShortcutContext.Provider value={value}>
      {children}

      <Modal
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        title="Keyboard shortcuts"
        description="Shortcuts are ignored while you are typing in a field."
        size="sm"
        footer={
          <Button variant="secondary" onClick={() => setSheetOpen(false)}>
            Close
          </Button>
        }
      >
        <dl className="divide-y divide-line">
          {rows.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-4 py-2.5">
              <dt className="text-sm text-fg-muted">{row.label}</dt>
              <dd className="flex shrink-0 items-center gap-1">
                {row.keys.map((key) => (
                  <kbd
                    key={key}
                    className="inline-flex min-w-7 items-center justify-center rounded-md border border-line-strong bg-sunken px-1.5 py-0.5 font-mono text-[11px] font-medium text-fg-muted"
                  >
                    {key}
                  </kbd>
                ))}
              </dd>
            </div>
          ))}
        </dl>
      </Modal>
    </ShortcutContext.Provider>
  )
}

export { KeyboardShortcuts }
